import React from 'react'
import { View, StyleSheet, ScrollView, TouchableOpacity } from 'react-native'
import { Button, Text, Modal, Icon, useTheme } from '@ui-kitten/components'
import { formatRelative } from 'date-fns'

import { useColors } from '../utils/useColors'
import { useTranslation } from 'react-i18next'

const cartItemNames = (cartItems) => {
  if (!cartItems || !Array.isArray(cartItems) || cartItems.length === 0) {
    return ''
  }
  var names = ''
  for (const [, value] of cartItems.entries()) {
    names = names + '\n' + value.name + ' ' + value.quantity
  }
  return names.slice(1)
}

export const SavedCartsModal = React.memo(function SavedCartsModal(props) {
  const { t } = useTranslation()
  const theme = useTheme()
  const colors = useColors()
  const iconFill = theme['text-basic-color']

  return (
    <Modal style={[styles.modal, { backgroundColor: theme['background-basic-color-1'] }]}
      visible={props.cartMenuVisible}
      backdropStyle={styles.backdrop}
      onBackdropPress={() => props.setCartMenuVisible(false)}
    >
      <ScrollView style={{flex: 1}} contentContainerStyle={styles.modalContainer}>
        <TouchableOpacity
          style={{position: 'absolute', top: 10, right: 10, alignSelf: 'flex-end', alignItems: 'center', justifyContent: 'center'}}
          onPress={() => {
            props.setCartMenuVisible(false)
          }}
        >
          <Icon name='close-circle' width={32} height={32} fill={iconFill} />
        </TouchableOpacity>
        <View style={[styles.row, {marginBottom: 20}]}>
          <Icon name='book-open-outline' width={32} height={32} fill={iconFill} style={{marginRight: 4, marginTop: 3}} />
          <Text category='h2' >
            {t('newSale.savedCarts')}
          </Text>
        </View>
        <View style={{flexDirection: 'row', flexWrap: 'wrap',}}>
          {props.savedCarts.map((item, index) => (
            <View key={item+index} testID={`saved-cart-${index}`} style={{flexGrow: 1, marginBottom: 10, marginRight: 10, padding: 20, borderWidth: 1, borderRadius: 4, borderColor: colors.borderGrey}} >
              <TouchableOpacity
                testID={`saved-cart-delete-${index}`}
                style={{alignSelf: 'flex-end', marginRight: -15, marginBottom: 0, marginTop: -10, alignItems: 'center', justifyContent: 'center', width: 20,}}
                onPress={() => props.onDeleteCart(item)}
              >
                <Icon name='trash-2-outline' width={15} height={15} fill={colors.red} />
              </TouchableOpacity>
              { item.tableNumber && item.tableNumber !== '' && (
                <View style={styles.row}>
                  <Icon name='hash-outline' width={16} height={16} fill={iconFill} style={styles.icon} />
                  <Text>{item.tableNumber}</Text>
                </View>
              )}
              <View style={styles.row}>
                <Icon name='clock-outline' width={16} height={16} fill={iconFill} style={styles.icon} />
                <Text>{item.dateCreated ? formatRelative(item.dateCreated.toDate ? item.dateCreated.toDate() : new Date(item.dateCreated), new Date(), {weekStartsOn: 1}) : ''}</Text>
              </View>
              <View style={styles.row}>
                <Icon name='person-outline' width={16} height={16} fill={iconFill} style={styles.icon} />
                <Text>{props.users[item.cashierUID]?.name}</Text>
              </View>
              { item.client != null && (
                <View style={styles.row}>
                  <Icon name='heart' width={16} height={16} fill={iconFill} style={styles.icon} />
                  <Text>{item.client?.displayName}</Text>
                </View>
              )}
              { item.comment && item.comment !== '' && (
                <View style={styles.row}>
                  <Icon name='message-square-outline' width={16} height={16} fill={iconFill} style={styles.icon} />
                  <Text>{item.comment}</Text>
                </View>
              )}
              { item.discountSelectedIndex !== 0 && (
                <View style={styles.row}>
                  <Icon name='percent-outline' width={16} height={16} fill={iconFill} style={styles.icon} />
                  <Text>{`${t('cartView.discount')}: ` + props.discountOptions[item.discountSelectedIndex]?.text}</Text>
                </View>
              )}
              { item.selectedPriceType && item.selectedPriceType.id !== null && (
                <View style={styles.row}>
                  <Icon name='pricetags-outline' width={16} height={16} fill={iconFill} style={styles.icon} />
                  <Text>{`${t('newSale.priceType')}: ` + item.selectedPriceType.name}</Text>
                </View>
              )}
              <View style={styles.row}>
                <Icon name='credit-card-outline' width={16} height={16} fill={iconFill} style={styles.icon} />
                <Text>{props.currencyFormatter.format(item.finalTotalCartPrice)}</Text>
              </View>
              <View style={[styles.row, {alignItems: 'flex-start', marginBottom: 10}]}>
                <Icon name='shopping-cart-outline' width={16} height={16} fill={iconFill} style={[styles.icon, {marginTop: 2}]} />
                <Text>
                  {cartItemNames(item.cartItems)}
                </Text>
              </View>
              <View testID={`saved-cart-select-${index}`}><Button status='basic' style={{marginTop: 'auto'}} onPress={() => {
                // `item.cartItems` is guaranteed to be an array by normalizeSavedCarts()
                // (utils/savedCart.js), applied on both savedCarts read paths in
                // NewSaleScreen. This is the only writer that puts external data into the
                // cart state, so that normalisation is what keeps `[...cartItems]` total.
                props.setCartItems(item.cartItems)
                if (item.client) {
                  props.setClient(item.client)
                  props.setBonusAction(item.bonusAction)
                } else {
                  props.setClient(null)
                }
                props.setComment(item.comment)
                props.setTableNumber(item.tableNumber)
                props.setDiscountSelectedIndex(item.discountSelectedIndex)
                props.setDiscountAmount(item.discountAmount ?? 0)
                if (props.setPaymentMethod) {
                  props.setPaymentMethod(item.paymentMethod || null)
                }
                props.setDelivery(item.delivery)
                props.setDeliveryPrice(item.deliveryPrice)
                props.setDeliveryPaidByCustomer(item.deliveryPaidByCustomer)
                props.setDeliveryAddress(item.deliveryAddress)
                props.setDeliveryIncludeInReceipt(item.deliveryIncludeInReceipt)
                // Resolve wallet ID string to full wallet object for PlatformDependantSelect
                if (item.deliveryPaymentSourceWallet && typeof item.deliveryPaymentSourceWallet === 'string') {
                  const walletObj = props.wallets?.find(w => w.id === item.deliveryPaymentSourceWallet || w.firestoreId === item.deliveryPaymentSourceWallet)
                  props.setDeliveryPaymentSourceWallet(walletObj || null)
                } else {
                  props.setDeliveryPaymentSourceWallet(item.deliveryPaymentSourceWallet)
                }
                // Restore price type if it was saved
                if (props.setSelectedPriceType && item.selectedPriceType !== undefined) {
                  props.setSelectedPriceType(item.selectedPriceType)
                  // Mark as manual selection if a non-default price type was saved
                  if (
                    props.setManualPriceTypeSelection &&
                    item.selectedPriceType &&
                    item.selectedPriceType.id !== null
                  ) {
                    props.setManualPriceTypeSelection(true)
                  }
                }
                props.setSavedCartApplied(item)
                props.setCartMenuVisible(false)
              }}>
                <Text>{t('select')}</Text>
              </Button></View>
            </View>
          ))}
        </View>
      </ScrollView>
    </Modal>
  )
}, function(prev, next) {
  if (prev.cartMenuVisible !== next.cartMenuVisible) {
    return false
  }
  if (prev.savedCarts.length !== next.savedCarts.length) {
    return false
  }
  if (prev.discountOptions.length !== next.discountOptions.length) {
    return false
  }
  return true
})

const styles = StyleSheet.create({
  modal: {
    top: 100,
    width: '80%',
    height: '80%',
    borderRadius: 20,
  },
  modalContainer: {
    alignItems: 'stretch',
    padding: 32,
  },
  spacedItem: {
    marginVertical: 4,
  },
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  icon: {
    marginRight: 4,
  },
})
